import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/auth/store';
import { useLogin } from '@/auth/useLogin';
import { ApiError } from '@/lib/api';

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const login = useLogin();

  // L12 — surfaced once the server replies `totp.required` to a
  // bare email/password attempt. The user is prompted for the OTP
  // code; the next submission re-uses the same email + password.
  const [otpRequired, setOtpRequired] = useState(false);

  // Build the zod schema with translated messages so field-level errors
  // also live in the user's chosen language.
  const schema = z.object({
    email: z
      .string()
      .min(1, t('auth.login.errors.emailRequired'))
      .email(t('auth.login.errors.emailInvalid')),
    password: z.string().min(1, t('auth.login.errors.passwordRequired')),
    totpCode: z
      .string()
      .optional()
      .refine(
        (v) => !otpRequired || (v !== undefined && /^[0-9]{6}$/.test(v)),
        { message: t('auth.login.errors.totpInvalid') },
      ),
  });
  type LoginValues = z.infer<typeof schema>;

  const form = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', totpCode: '' },
  });

  // Where to redirect after a successful login: the page the user
  // originally requested, or /dashboard as a default.
  const fromPath = (location.state as LocationState | null)?.from?.pathname;
  const redirectTo = fromPath && fromPath !== '/login' ? fromPath : '/dashboard';

  useEffect(() => {
    if (user) navigate(redirectTo, { replace: true });
  }, [user, redirectTo, navigate]);

  if (user) return <Navigate to={redirectTo} replace />;

  const errorMessage = (() => {
    const e = login.error;
    if (!e) return null;
    if (e instanceof ApiError) {
      // L12 — `totp.required` flips the form into OTP-prompt mode
      // rather than rendering a generic error.
      if (e.key === 'totp.required') return null;
      // The backend i18n key is mirrored on the frontend under apiError.*
      return t(e.key, { defaultValue: t('apiError.unknown', { status: e.statusCode ?? '?' }), ...(e.params ?? {}) });
    }
    return t('apiError.unknown', { status: '?' });
  })();

  const submit = form.handleSubmit((values) => {
    login.mutate(
      {
        email: values.email,
        password: values.password,
        ...(values.totpCode ? { totpCode: values.totpCode } : {}),
      },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.key === 'totp.required') {
            setOtpRequired(true);
          }
        },
      },
    );
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3 text-center">
          <img
            src="/icon-192x192.png"
            alt="ISI Marine Services"
            className="mx-auto h-20 w-auto"
          />
          <CardTitle className="text-3xl text-primary">
            {t('common.appName')}
          </CardTitle>
          <CardDescription>{t('auth.login.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={submit} className="space-y-4" noValidate>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.login.emailLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        autoFocus
                        placeholder={t('auth.login.emailPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.login.passwordLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="current-password"
                        placeholder={t('auth.login.passwordPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {otpRequired && (
                <FormField
                  control={form.control}
                  name="totpCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.login.totpLabel')}</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          autoFocus
                          placeholder="123456"
                          data-testid="login-totp-input"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value.replace(/\D/g, '').slice(0, 6),
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {errorMessage && (
                <Alert variant="destructive">
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={login.isPending}>
                {login.isPending && <Loader2 className="animate-spin" aria-hidden />}
                {login.isPending ? t('auth.login.submitting') : t('auth.login.submit')}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </main>
  );
}
